import React from 'react';

import {
  BillingCycle,
  Coupon,
  CouponType,
  Plan,
  PriceTierFragment,
  SubscriptionStatus,
  SubscriptionSubstatus,
} from '$/GraphQL/schema/schema';
import { formatCurrency } from '$/utils/formatCurrency';

import pluralize from 'pluralize';
import { Per, SubscriptionNote } from './AddOns/Components';
import { usePlanPageContext } from './PlanPage';
import RadioButton from '$/components/ui/RadioButton';

const SwitchingPlanRadioButton: React.FC<{ billingCycle: BillingCycle }> = ({ billingCycle }) => {
  const context = usePlanPageContext();
  if (context.subscriptionInfo.isLegacyTeam) return null;
  if (context.subscriptionInfo.isCancelled) return null;
  if (context.subscriptionInfo.subStatus === SubscriptionSubstatus.Pending) return null;
  return (
    <div className='hidden md:flex width-full justify-center'>
      <label key={billingCycle} className='mr-1'>
        <div className='flex items-center mb-2 cursor-pointer'>
          <RadioButton.Option
            value={billingCycle}
            checked={context.billingCycle === billingCycle}
            onChange={(e): void => {
              if ([SubscriptionStatus.PastDue].includes(context.subscriptionInfo.status)) {
                return;
              }
              context.setBillingCycle(e.target.value);
            }}
          />
          <RadioButton />
        </div>
      </label>
    </div>
  );
};
export class PriceTierInfo {
  public constructor(
    protected price: PriceTierFragment,
    protected coupon?: Coupon
  ) {}

  public get name(): string {
    return this.price.name;
  }

  public get tierId(): string {
    return this.price.tierId;
  }

  public get to(): number {
    return this.price.to;
  }

  public get registrantLimit(): number {
    return this.price.registrantLimit ?? 0;
  }

  public get monthlyPrice(): number {
    return this.price.monthlyPrice;
  }

  public get monthlyPriceWithCoupon(): number {
    return PriceTierInfo.applyCoupon(this.monthlyPrice, this.coupon);
  }

  public get annualPrice(): number {
    return this.price.annualPrice;
  }

  public get annualPriceWithCoupon(): number {
    return PriceTierInfo.applyCoupon(this.annualPrice, this.coupon);
  }

  public get annualPricePerMonth(): number {
    return this.price.annualPricePerMonth;
  }

  public get pricePerAdditional(): number {
    return this.price.pricePerAdditional ?? 0;
  }

  public get annualPricePerAdditional(): number {
    return this.price.annualPricePerAdditional ?? 0;
  }

  public get annualPricePerAdditionalPerMonth(): number {
    return this.price.annualPricePerAdditionalPerMonth ?? 0;
  }

  public get noOfWebinarsText(): React.ReactElement {
    let message = `Up to 15 active webinars`;
    switch (this.price.tierId) {
      case Plan.Level1:
        message = `Up to 1 active webinar`;
        break;
      case Plan.Level2:
        message = `Up to 5 active webinars`;
        break;
    }
    return <>{message}</>;
  }

  public get registrantLimitText(): React.ReactElement {
    return <>{this.registrantLimit.toLocaleString()} registrants/month</>;
  }

  public getPriceText(isAnnual: boolean): string {
    const price = isAnnual ? this.annualPrice : this.monthlyPrice;
    return PriceTierInfo.format(price);
  }

  public getPriceWithCoupon(isAnnual: boolean): string {
    const price = isAnnual ? this.annualPriceWithCoupon : this.monthlyPriceWithCoupon;
    return PriceTierInfo.format(price);
  }

  public getFeatureTextShort(isAnnual: boolean): React.ReactElement {
    const unitPriceString = isAnnual ? this.annualPricePerAdditional : this.pricePerAdditional;
    const per = isAnnual ? '/year' : '/month';
    if (this.tierId === Plan.Level3) {
      return (
        <>
          {this.noOfWebinarsText}
          <br />+ {PriceTierInfo.format(unitPriceString)}
          {per} for each additional
          <br />
          {this.registrantLimitText}
        </>
      );
    }
    return (
      <>
        {this.noOfWebinarsText}
        <br /> {this.registrantLimitText}
      </>
    );
  }

  public getSummary(totalPublished: number, activeUntil?: string): React.ReactElement {
    let summary = (
      <>
        <tr>
          <td>
            <span className='text-lg font-semibold pt-2e'>{this.name} subscription</span>
          </td>
          <td>
            <SwitchingPlanRadioButton billingCycle={BillingCycle.Month} />
          </td>
          <td>
            <SwitchingPlanRadioButton billingCycle={BillingCycle.Year} />
          </td>
        </tr>
        <tr>
          <td>
            {this.noOfWebinarsText}
            {activeUntil && (
              <>
                <br />
                <SubscriptionNote>Active until {activeUntil}</SubscriptionNote>
              </>
            )}
          </td>
          <td className='price'>
            {formatCurrency(this.monthlyPrice)}
            <Per />
          </td>
          <td className='price'>
            {formatCurrency(this.annualPricePerMonth)}
            <Per />
          </td>
        </tr>
      </>
    );

    switch (this.tierId) {
      case Plan.Level3: {
        const additionalWebinars = Math.max(0, totalPublished - this.price.to);
        summary = (
          <>
            {summary}
            <tr>
              <td>
                {additionalWebinars} additional active {pluralize('webinar', additionalWebinars)}{' '}
              </td>
              <td className='price'>
                {formatCurrency(additionalWebinars * this.pricePerAdditional)}
                <Per />
                <div className='text-sm text-gray-1 italic leading-1e'>
                  @ {formatCurrency(this.pricePerAdditional)} each
                </div>
              </td>
              <td>
                {formatCurrency(additionalWebinars * this.annualPricePerAdditionalPerMonth)}
                <Per />
                <div className='text-sm text-gray-1 italic leading-1e'>
                  @ {formatCurrency(this.annualPricePerAdditionalPerMonth)} each
                </div>
              </td>
            </tr>
          </>
        );
      }
    }
    return summary;
  }

  public static applyCoupon(price: number, coupon?: Coupon): number {
    if (!coupon || coupon.type === CouponType.Custom) return price;
    return Math.round(price * coupon.percent_off!) / 100 - coupon.amount_off! / 100;
  }

  public static format(price: number): string {
    return formatCurrency(price);
  }
}
